import type { ReactNode } from 'react';
import { Button } from '../Button';
import './SaveBar.css';

/**
 * SaveBar — 저장 줄 한 벌 (가이드 P2-1 ④ · web 레인 W8 · 계획 F5(b) · 평 2차 «저장 단추 자리가 여섯»).
 *
 * 꼴: 왼쪽 [덧말 · 상태 글] ······ [켜지는 조건] [보조] [되돌리기] [저장]
 *   · 바닥 꼴은 설정 화면 저장바와 같다(`.wc-set__foot` — 아래 붙박이 · 위 선 · 종이 바탕).
 *   · 상태 글을 화면이 주지 않으면: 바뀐 것이 없으면 «바뀐 것이 없습니다», 있으면
 *     «N곳 바뀜 · 저장하지 않았습니다»(수를 모르면 «바뀐 것이 있습니다 · 저장하지 않았습니다»).
 *   · 저장이 꺼져 있으면 «켜지는 조건» 한 줄을 단추 옆에 적는다(시안 2차 R1 — 꺼진 단추만 두면
 *     «켜졌는데 안 눌린다» 로 읽힌다). 기본은 «칸을 고치면 켜집니다».
 * ★ 저장 · 되돌리기 «함수와 조건은 화면이 준다» — 이 부품은 그리기만 한다(화면의 검증 · 막는 조건이 그대로 산다).
 * ★ 글은 한국어 기본값이다 — 여러 나라 말을 쓰는 화면은 글 prop 을 모두 넘긴다.
 */
export interface SaveBarProps {
  /** 저장할 수 있나 — 화면의 모든 조건을 합친 값(바뀜 · 검증 · 저장 중 아님 등) */
  canSave: boolean;
  onSave: () => void;
  /** 저장 단추 글 — 기본 «저장» */
  saveLabel?: ReactNode;
  /** 저장 단추 앞 아이콘 */
  saveIcon?: ReactNode;
  /** 저장 중 — 저장 단추가 도는 꼴(꺼진 꼴이 아니다) */
  saving?: boolean;
  /** 되돌리기 — 주면 단추가 선다 */
  onRevert?: () => void;
  /** 되돌릴 수 있나 — 기본은 바뀐 것이 있고 저장 중이 아닐 때 */
  canRevert?: boolean;
  revertLabel?: ReactNode;
  revertIcon?: ReactNode;
  /** 바뀐 것이 있나 — 기본 상태 글을 고르는 데만 쓴다 */
  dirty?: boolean;
  /** 바뀐 수 — 주면 «N{단위} 바뀜» */
  changedCount?: number;
  /** 바뀐 수의 단위 — 기본 «곳» */
  changedUnit?: string;
  /** 화면이 정한 상태 글(저장 중 · 저장됨 · 실패 등) — 주면 기본 글 대신 선다 */
  message?: ReactNode;
  messageTone?: 'ok' | 'bad';
  messageRole?: 'status' | 'alert';
  /** 상태 글 앞 덧말(«변경은 감사에 남습니다» 처럼 늘 서는 안내) */
  note?: ReactNode;
  /** 저장이 꺼졌을 때 켜지는 조건 — null 이면 적지 않는다. 기본 «칸을 고치면 켜집니다» */
  enableHint?: ReactNode;
  /** 되돌리기 자리 앞의 보조 행동(«취소» 링크 등) */
  secondary?: ReactNode;
  /** 화면이 바닥 꼴에 덧대는 클래스(카드 안 여백 등) */
  className?: string;
}

function defaultMessage(dirty: boolean | undefined, count: number | undefined, unit: string): string {
  if (count !== undefined) return count > 0 ? `${count}${unit} 바뀜 · 저장하지 않았습니다` : '바뀐 것이 없습니다';
  if (dirty === undefined) return '';
  return dirty ? '바뀐 것이 있습니다 · 저장하지 않았습니다' : '바뀐 것이 없습니다';
}

export function SaveBar({
  canSave, onSave, saveLabel = '저장', saveIcon, saving = false,
  onRevert, canRevert, revertLabel = '되돌리기', revertIcon,
  dirty, changedCount, changedUnit = '곳',
  message, messageTone, messageRole = 'status', note,
  enableHint = '칸을 고치면 켜집니다', secondary, className,
}: SaveBarProps) {
  const changed = changedCount !== undefined ? changedCount > 0 : dirty;
  const msg = message ?? defaultMessage(dirty, changedCount, changedUnit);
  const revertOn = canRevert ?? (Boolean(changed) && !saving);
  const showHint = !canSave && !saving && enableHint != null;
  const tone = messageTone ? ` is-${messageTone}` : '';
  return (
    <div className={['wc-set__foot', 'wc-savebar', className ?? ''].filter(Boolean).join(' ')}>
      <div className="wc-savebar__left">
        {note != null && <span className="wc-savebar__note">{note}</span>}
        <p className={`wc-set__footmsg${tone}`} role={messageRole}>{msg}</p>
      </div>
      {showHint && <p className="wc-savebar__why">{enableHint}</p>}
      {secondary}
      {onRevert && (
        <Button variant="secondary" iconLeft={revertIcon} onClick={onRevert} disabled={!revertOn}>
          {revertLabel}
        </Button>
      )}
      <Button variant="primary" iconLeft={saveIcon} onClick={onSave} disabled={!canSave} loading={saving}>
        {saveLabel}
      </Button>
    </div>
  );
}
